import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar
} from '@mui/material';
import {
  EmojiEvents,
  Speed,
  LocalFireDepartment,
  School,
  Person,
  People,
  PlayArrow,
  Leaderboard,
  Stars
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getLevelInfo, getLeaderboard } from '../../services/gameService';
import { useNavigate } from 'react-router-dom';

const MotionPaper = motion(Paper);

function Dashboard() {
  const { currentUser } = useAuth();
  const [currentLevelInfo, setCurrentLevelInfo] = useState(null);
  const [nextLevelInfo, setNextLevelInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [levelData, leaderboardData] = await Promise.all([
          getLevelInfo(),
          getLeaderboard()
        ]);
        
        const currentLevel = levelData.find(l => 
          parseInt(l.name.split(' ')[1]) === (currentUser.level || 1)
        );
        setCurrentLevelInfo(currentLevel);

        const nextLevel = levelData.find(l => 
          parseInt(l.name.split(' ')[1]) === (currentUser.level || 1) + 1
        );
        setNextLevelInfo(nextLevel);

        setLeaderboard(leaderboardData);
      } catch (error) {
        console.error('Veri yüklenirken hata:', error);
      }
    };

    loadData();
  }, [currentUser.level]);

  const calculateProgress = () => {
    if (!currentLevelInfo || !nextLevelInfo) return 0;
    
    const currentScore = currentUser.score || 0;
    const currentLevelScore = currentLevelInfo.requiredScore;
    const nextLevelScore = nextLevelInfo.requiredScore;
    
    if (!nextLevelInfo) return 100;
    
    const levelDiff = nextLevelScore - currentLevelScore;
    const userProgress = currentScore - currentLevelScore;
    
    return Math.min(Math.max((userProgress / levelDiff) * 100, 0), 100);
  };

  const calculateRemainingPoints = () => {
    if (!nextLevelInfo) return 0;
    return Math.max(nextLevelInfo.requiredScore - (currentUser.score || 0), 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Kullanıcı Profili ve Seviye Kartı */}
        <Grid item xs={12} md={4}>
          <MotionPaper
            elevation={3}
            sx={{ p: 3, textAlign: 'center' }}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Avatar
              sx={{
                width: 100,
                height: 100,
                margin: '0 auto',
                bgcolor: 'primary.main',
                fontSize: '2.5rem',
                mb: 2
              }}
            >
              {currentUser?.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {currentUser?.username}
            </Typography>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              bgcolor: 'primary.light', 
              px: 2, 
              py: 0.5, 
              borderRadius: 2,
              mb: 2
            }}>
              <School sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Seviye {currentUser.level || 1}
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={calculateProgress()}
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  bgcolor: 'rgba(0, 191, 13, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                    borderRadius: 5
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {calculateRemainingPoints()} puan kaldı
              </Typography>
            </Box>
          </MotionPaper>
        </Grid>

        {/* Oyun Modları */}
        <Grid item xs={12} md={8}>
          <MotionPaper
            elevation={3}
            sx={{ p: 3 }}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Oyun Modları
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                  onClick={() => navigate('/play/single')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6">Tek Oyunculu</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Kendi hızında öğren
                        </Typography>
                      </Box>
                    </Box>
                    <Button 
                      variant="contained" 
                      fullWidth
                      startIcon={<PlayArrow />}
                      sx={{
                        background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                      }}
                    >
                      Oyna
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                  onClick={() => navigate('/play/duo')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <People sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6">Çok Oyunculu</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Arkadaşlarınla yarış
                        </Typography>
                      </Box>
                    </Box>
                    <Button 
                      variant="contained" 
                      fullWidth
                      startIcon={<PlayArrow />}
                      sx={{
                        background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                      }}
                    >
                      Oyna
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </MotionPaper>
        </Grid>

        {/* Puan Sistemi */}
        <Grid item xs={12} md={6}>
          <MotionPaper
            elevation={3}
            sx={{ p: 3 }}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Puan Sistemi
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <EmojiEvents sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Temel Puan" 
                  secondary="Her doğru cevap için 10 puan"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Speed sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Hız Bonusu" 
                  secondary="Her saniye için +2 puan bonus"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LocalFireDepartment sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Combo Bonusu" 
                  secondary="Art arda doğrular için +5 puan"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Stars sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Seviye Atlama" 
                  secondary="Yeni seviyede +50 bonus puan"
                />
              </ListItem>
            </List>
          </MotionPaper>
        </Grid>

        {/* Liderlik Tablosu */}
        <Grid item xs={12} md={6}>
          <MotionPaper
            elevation={3}
            sx={{ p: 3 }}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <Leaderboard sx={{ mr: 1 }} /> Liderlik Tablosu
            </Typography>
            <List>
              {leaderboard.slice(0, 5).map((user, index) => (
                <ListItem key={user.id} sx={{ 
                  bgcolor: user.id === currentUser.uid ? 'primary.light' : 'transparent',
                  borderRadius: 1
                }}>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: index < 3 ? 'primary.main' : 'grey.400' }}>
                      {index + 1}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary={user.username}
                    secondary={`${user.score || 0} puan`}
                  />
                  <Typography variant="body2" color="primary">
                    Seviye {user.level || 1}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </MotionPaper>
        </Grid>

        {/* İstatistikler */}
        <Grid item xs={12}>
          <MotionPaper
            elevation={3}
            sx={{ p: 3 }}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              İstatistikler
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: 'primary.light' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Toplam Puan
                    </Typography>
                    <Typography variant="h4">
                      {currentUser.score || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: 'primary.light' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Seviye
                    </Typography>
                    <Typography variant="h4">
                      {currentUser.level || 1}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: 'primary.light' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      En Yüksek Combo
                    </Typography>
                    <Typography variant="h4">
                      {currentUser.maxCombo || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: 'primary.light' }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Doğru Sayısı
                    </Typography>
                    <Typography variant="h4">
                      {currentUser.correctAnswers || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </MotionPaper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
